"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FileSearch, MessageSquare, ShieldCheck, UserCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guarantees = [{
  icon: UserCheck,
  title: "Make the right hiring decision",
  description: "I help you evaluate whether hiring an independent SEO consultant fits your stage, budget, and growth targets before any contract is signed."
}, {
  icon: ShieldCheck,
  title: "Transparent accountability",
  description: "Honest benchmarks and clear deliverables. If organic search is not your best growth lever right now, I tell you immediately."
}, {
  icon: CheckCircle2,
  title: "Practical execution",
  description: "Concrete actions tailored to your industry, architecture, and revenue targets, rather than automated 80-page fluff."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="closingconsultationcta" data-nav="light" className="relative bg-background py-20 md:py-28 lg:py-32 overflow-hidden">
      {/* Subtle decorative background tints using theme primary and secondary blush */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 right-1/2 translate-x-1/2 w-[720px] h-[340px] rounded-full bg-secondary/40 blur-3xl opacity-70" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 right-10 w-96 h-80 rounded-full bg-primary/5 blur-3xl opacity-60" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto max-w-4xl">
          {/* Badge indicator */}
          <motion.div variants={itemVariants} className="flex justify-center mb-6">
            <Badge variant="secondary" className="px-3.5 py-1.5 text-xs font-medium tracking-normal border border-accent/30 bg-secondary/60 text-foreground">
              SEO hiring consultation
            </Badge>
          </motion.div>

          {/* Main heading */}
          <motion.h2 variants={itemVariants} className="text-center font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Ready to Discuss Your SEO Goals?
          </motion.h2>

          {/* Supporting line */}
          <motion.p variants={itemVariants} className="mt-5 text-center text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto font-sans leading-relaxed">
            Tell me about your website, current search roadblocks, and where you want your business to be. I will review your performance and outline a clear path forward.
          </motion.p>

          {/* Dual Action Buttons */}
          <motion.div variants={itemVariants} className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-7 text-sm font-semibold rounded-md shadow-md bg-primary text-primary-foreground hover:bg-primary/90 transition-[background-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <MessageSquare className="size-4" />
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto h-12 px-7 text-sm font-semibold rounded-md border-border bg-card text-foreground hover:bg-accent/10 hover:border-accent/40 transition-[background-color,border-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <FileSearch className="size-4 text-primary" />
                <span>Get a Free SEO Audit</span>
              </Link>
            </Button>
          </motion.div>

          {/* Direct Collaboration Features */}
          <motion.div variants={containerVariants} className="mt-14 sm:mt-18 pt-10 border-t border-border/80 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {guarantees.map((item, i) => {
            const IconComponent = item.icon;
            return <div key={item.title} data-index={i} className="rounded-lg bg-card p-6 border border-border/70 shadow-xs flex flex-col items-start">
                  <div className="flex items-center justify-center size-10 rounded-md bg-secondary text-primary mb-4 border border-accent/20">
                    <IconComponent className="size-5" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground font-default">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground font-sans leading-relaxed">
                    {item.description}
                  </p>
                </div>;
          })}
          </motion.div>

          {/* Reassuring personal signoff */}
          <motion.div variants={itemVariants} className="mt-10 text-center">
            <p className="text-xs sm:text-sm text-muted-foreground">
              Consultations available worldwide · Based in Swindon, UK.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}